import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const PasswordLogin = () => {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [pid, setPid] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotPid, setForgotPid] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pid || !pw) { toast.error('Enter ID and password'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/auth/password/login', { personal_id: pid.trim().toUpperCase(), password: pw });
      if (data?.session_token) localStorage.setItem('session_token', data.session_token);
      await refresh?.();
      toast.success(`Welcome, ${data?.user?.name || data?.user?.personal_id}`);
      window.location.href = '/store';
    } catch (e2) {
      const msg = e2.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg.replace(/_/g, ' ') : 'Login failed');
    } finally { setBusy(false); }
  };

  const submitForgot = async () => {
    if (!forgotPid) return;
    try {
      await api.post('/auth/password/forgot', { personal_id: forgotPid.trim().toUpperCase() });
      toast.success('If that ID exists, a reset link has been generated for admin to relay.');
      setForgotOpen(false);
      setForgotPid('');
    } catch { toast.error('Could not submit request'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] to-[#1D4ED8] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 shadow-2xl" data-testid="login-card">
        <div className="text-center mb-4">
          <h1 className="text-3xl font-black display text-[#1E3A8A]">INDIVALIFE</h1>
          <p className="text-xs text-[var(--bz-muted)] mt-1">Sign in to your distributor dashboard</p>
        </div>

        <div className="mt-4">
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Personal ID</Label>
                <Input value={pid} onChange={e => setPid(e.target.value.toUpperCase())} placeholder="e.g. IL10001" className="font-mono-bz" data-testid="login-pid-input" autoFocus />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Your password" data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] h-11 font-bold display" data-testid="login-submit-btn">
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => setForgotOpen(true)} className="text-[#1D4ED8] underline" data-testid="forgot-pw-btn">Forgot password?</button>
                <Link to="/" className="text-[var(--bz-muted)]">← Back to home</Link>
              </div>
            </form>
        </div>

        {forgotOpen && (
          <Card className="mt-4 p-3 bg-[var(--bz-soft)] space-y-2" data-testid="forgot-pw-panel">
            <Label>Enter your Personal ID</Label>
            <Input value={forgotPid} onChange={e => setForgotPid(e.target.value.toUpperCase())} placeholder="IL10001" className="font-mono-bz" data-testid="forgot-pid-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={submitForgot} className="bg-[#1D4ED8]" data-testid="submit-forgot-btn">Send reset link</Button>
              <Button size="sm" variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
            </div>
            <p className="text-[10px] text-[var(--bz-muted)]">Until email delivery is wired in, an admin will relay your link via WhatsApp.</p>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default PasswordLogin;
